import { MeasureKind, UnitsByMeasure } from 'convert';
import { EAttributeType } from '../../@types/AttributeTypes'; // Added EAttributeUnitFamily
import { IBuildingProviderContext } from '../../@types/ContextProviderRelated';
import { EUnitType, EWall } from '../../@types/Enums';
import { EHistoryAction } from '../../hooks/useUndoRedo';
import { IWall } from '../../models/abstract/Wall';
import AttributeModel, { IAttribute } from '../../models/standard/AttributeModel';
import FloorModel from '../../models/standard/FloorModel';
import PhysicalBoundaryModel, { BoundaryEdge } from '../../models/standard/PhysicalBoundary';
import StandardWallModel from '../../models/standard/StandardWallModel';
import { INTERNAL_TO_CM_FACTOR } from '../constants';
import { convertTo } from '../conversionUtils';
import filesApiService from '../filesApiService';
import { DefaultAttributeId, DEFAULT_ATTRIBUTES, ObjectType } from './defaultObjectsAndAttributes';

interface HasFilesApiId {
  filesApiId?: number;
  uuid?: string;
}

export async function fetchAttributesForObject(obj: HasFilesApiId, objectType: ObjectType, region?: string): Promise<AttributeModel[]> {
  if (!obj?.filesApiId) return [];
  try {
    // Pass region to the API service to get correct defaults
    const details = await filesApiService.getObjectById(obj.filesApiId, region);

    if (!details || !details.attributes) return [];

    const fetchedAttributes = details.attributes.map(
      (a: any, i: number) =>
        new AttributeModel(
          a.id,
          a.key,
          a.value,
          i === 0 ? null : details.attributes[i - 1].id,
          a.type as EAttributeType,
          a.unit,
          a.hidden || false,
          a.forced || false,
          // --- Pass new properties from API ---
          a.ownerId,
          a.userId,
          a.publicFlag,
          a.localization,
          a.defaults,
          a.validation,
          a.unitFamily,
        ),
    );

    return applyDefaultAttributeFlags(objectType, fetchedAttributes, obj.uuid);
  } catch (err) {
    console.error('Error loading attributes', err);
    return [];
  }
}
export async function assignAttributesToImportedPanoMeasurements(
  building: IBuildingProviderContext, 
  region?: string
): Promise<IBuildingProviderContext> {
  if (!building.rooms || building.rooms.length === 0) return building;

  const allUpdatedWalls: StandardWallModel[] = [];

  // We loop through existing rooms and UPDATE them in place
  await Promise.all(building.rooms.map(async (room) => {
    const space = room.spaces?.[0];
    const floor = space?.floor;

    if (!space || !floor) return;

    // 1. Fetch Attributes
    const roomPromise = room.filesApiId 
      ? fetchAttributesForObject({ filesApiId: room.filesApiId }, ObjectType.Room, region)
      : Promise.resolve([]);
    const floorPromise = floor.filesApiId 
      ? fetchAttributesForObject({ filesApiId: floor.filesApiId }, ObjectType.Floor, region)
      : Promise.resolve([]);

    const spaceWalls = (space.walls || []).filter(w => w instanceof StandardWallModel) as StandardWallModel[];
    const wallPromises = spaceWalls.map(w => {
      if (!w.filesApiId) return Promise.resolve([]);
      return fetchAttributesForObject({ filesApiId: w.filesApiId }, ObjectType.Wall, region).catch(() => []);
    });

    const [roomAttrs, floorAttrs, ...wallsAttrs] = await Promise.all([roomPromise, floorPromise, ...wallPromises]);

    // 2. Update ROOM Attributes (MERGED!)
    room.attributes = mergeAttributes(room.attributes, roomAttrs);

    // 3. Update FLOOR Attributes (MERGED!)
    const mergedFloorAttrs = mergeAttributes(floor.attributes, floorAttrs);
    const updatedFloor = new FloorModel(floor.tiles, floor.texture, mergedFloorAttrs, floor.uuid);
    
    if (floor.filesApiId) (updatedFloor as any).filesApiId = floor.filesApiId;
    space.floor = updatedFloor; // Update reference on Space

    // 4. Update WALLS (MERGED!)
    const wallMap = new Map<string, StandardWallModel>();
    const newSpaceWalls: StandardWallModel[] = [];

    spaceWalls.forEach((wall, i) => {
      const attrs = wallsAttrs[i] || [];
      
      // Use the helper to merge existing wall attributes with the fetched ones
      const mergedAttrs = mergeAttributes(wall.attributes, attrs);
      const updatedGeometry = updateWallGeometryAttributes(wall, EUnitType.METRIC, mergedAttrs);
      
      const newWall = new StandardWallModel(
        wall.startPoint, wall.endPoint, wall.depth, 
        wall.windows, wall.doors, [], 
        updatedGeometry, wall.uuid, wall.passages
      );
      newWall.sides.A.radiators = wall.sides.A.radiators;
      newWall.sides.B.radiators = wall.sides.B.radiators;
      if (wall.filesApiId) (newWall as any).filesApiId = wall.filesApiId;

      wallMap.set(wall.uuid, newWall);
      newSpaceWalls.push(newWall);
      allUpdatedWalls.push(newWall);
    });

    // 5. Update BOUNDARIES (Mutation)
    if (space.boundaries) {
        space.boundaries.forEach(boundary => {
            if (boundary instanceof PhysicalBoundaryModel) {
                // Update the edges to point to the new walls
                const updatedEdges = boundary.edges.map((edge) => {
                    const newWall = wallMap.get(edge.wall.uuid);
                    return newWall 
                        ? new BoundaryEdge(newWall, edge.t0, edge.t1, edge.side, edge.reversed) 
                        : edge;
                });
                // Update the boundary internals
                boundary.setEdges(updatedEdges);
                // If boundary has a walls list, update it too
                if ((boundary as any).walls) (boundary as any).walls = newSpaceWalls;
            }
        });
    }
    space.walls = newSpaceWalls;

  }));

  // Return the SAME building object (mutated)
  return {
    ...building,
    walls: allUpdatedWalls, // Update the global wall list
    lastAction: EHistoryAction.ROOM_ATTRIBUES_UPDATED,
  };
}

export function applyDefaultAttributeFlags(objectType: ObjectType, fetchedAttributes: AttributeModel[], uuid?: string): AttributeModel[] {
  const defaultAttributes = DEFAULT_ATTRIBUTES[objectType] || [];

  const defaultAttributeMap = new Map<number, { hidden: boolean; forced: boolean }>();
  defaultAttributes.forEach(({ id, hidden, forced }) => {
    defaultAttributeMap.set(id, { hidden, forced });
  });

  // Patch the attributes
  const attributesWithFlags = fetchedAttributes.map((attr) => {
    const defaults = defaultAttributeMap.get(attr.id);

    const isIdAttr =
      (objectType === ObjectType.Room && attr.id === DefaultAttributeId.ROOM_ID) ||
      (objectType === ObjectType.Wall && attr.id === DefaultAttributeId.WALL_ID) ||
      (objectType === ObjectType.Door && attr.id === DefaultAttributeId.DOOR_ID) ||
      (objectType === ObjectType.Window && attr.id === DefaultAttributeId.WINDOW_ID) ||
      (objectType === ObjectType.Radiator && attr.id === DefaultAttributeId.RADIATOR_ID) ||
      (objectType === ObjectType.Floor && attr.id === DefaultAttributeId.FLOOR_ID);

    const type = isIdAttr ? EAttributeType.TEXT : attr.type;
    const value = isIdAttr && uuid ? uuid : attr.value;

    return new AttributeModel(
      attr.id,
      attr.key,
      value,
      attr.predecessorId,
      type,
      attr.unit,
      defaults?.hidden ?? false,
      defaults?.forced ?? false,
      // --- Pass existing properties to preserve them ---
      attr.ownerId,
      attr.userId,
      attr.publicFlag,
      attr.localization,
      attr.defaults,
      attr.validation,
      attr.unitFamily,
    );
  });

  // Order attributes
  const forcedAttrs = attributesWithFlags.filter((attr) => attr.forced && !attr.hidden);
  const normalAttrs = attributesWithFlags.filter((attr) => !attr.forced && !attr.hidden);
  const hiddenAttrs = attributesWithFlags.filter((attr) => attr.hidden);

  const orderedAttrs = [...forcedAttrs, ...normalAttrs, ...hiddenAttrs].map((attr, index, arr) => {
    const predecessorId = index === 0 ? null : arr[index - 1].id;

    // We must pass all properties again to preserve them during reordering
    return new AttributeModel(
      attr.id,
      attr.key,
      attr.value,
      predecessorId,
      attr.type,
      attr.unit,
      attr.hidden,
      attr.forced,
      // --- Pass preserved properties ---
      attr.ownerId,
      attr.userId,
      attr.publicFlag,
      attr.localization,
      attr.defaults,
      attr.validation,
      attr.unitFamily,
    );
  });
  return orderedAttrs;
}

/**
 * Updates or initializes WALL_THICKNESS, WALL_TYPE, and WALL_LENGTH attributes for a wall.
 */
export function updateWallGeometryAttributes(wall: IWall, unitType: EUnitType, existingAttrs: AttributeModel[] = []): AttributeModel[] {
  const wallLength = wall.startPoint.distanceTo(wall.endPoint);
  const lengthCm = wallLength * INTERNAL_TO_CM_FACTOR;
  const lengthValue =
    unitType === EUnitType.METRIC
      ? parseFloat(lengthCm.toFixed(2))
      : Math.round(convertTo(lengthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
  const lengthUnit = unitType === EUnitType.METRIC ? 'cm' : 'in';

  const thicknessCm = wall.depth * INTERNAL_TO_CM_FACTOR;
  const thicknessValue =
    unitType === EUnitType.METRIC
      ? parseFloat(thicknessCm.toFixed(2))
      : Math.round(convertTo(thicknessCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
  const thicknessUnit = unitType === EUnitType.METRIC ? 'cm' : 'in';

  const wallType = wall.type === EWall.STANDARD ? 'Standardwand' : 'Zwischenwand';

  // Helper to copy ALL properties
  function toAttributeModel(attr: any): AttributeModel {
    if (attr instanceof AttributeModel) return attr;

    // If it's a plain object (deserialized JSON), reconstruct full Model
    return new AttributeModel(
      attr.id,
      attr.key,
      attr.value,
      attr.predecessorId,
      attr.type,
      attr.unit,
      attr.hidden,
      attr.forced,
      attr.ownerId,
      attr.userId,
      attr.publicFlag,
      attr.localization,
      attr.defaults,
      attr.validation,
      attr.unitFamily,
    );
  }

  const updatedAttrs =
    existingAttrs.length > 0
      ? existingAttrs.map(toAttributeModel)
      : [
          // Defaults do not have localization/validation usually, so undefined is fine
          new AttributeModel(DefaultAttributeId.WALL_THICKNESS, 'WALL_THICKNESS', 0, null, EAttributeType.NUMBER, thicknessUnit, false, true),
          new AttributeModel(DefaultAttributeId.WALL_TYPE, 'WALL_TYPE', '', null, EAttributeType.TEXT, null, true, false),
          new AttributeModel(DefaultAttributeId.WALL_LENGTH, 'WALL_LENGTH', 0, null, EAttributeType.NUMBER, lengthUnit, false, true),
        ];

  return updatedAttrs.map((attr) => {
    // For the specific geometry attributes, we update the value/unit
    // BUT we must preserve other flags (localization, etc) if they exist on the 'attr' object
    if (attr.id === DefaultAttributeId.WALL_THICKNESS) {
      return new AttributeModel(
        attr.id,
        attr.key,
        thicknessValue,
        attr.predecessorId,
        EAttributeType.NUMBER,
        thicknessUnit,
        attr.hidden,
        attr.forced,
        attr.ownerId,
        attr.userId,
        attr.publicFlag,
        attr.localization,
        attr.defaults,
        attr.validation,
        attr.unitFamily,
      );
    }
    if (attr.id === DefaultAttributeId.WALL_TYPE) {
      return new AttributeModel(
        attr.id,
        attr.key,
        wallType,
        attr.predecessorId,
        EAttributeType.TEXT,
        null,
        attr.hidden,
        attr.forced,
        attr.ownerId,
        attr.userId,
        attr.publicFlag,
        attr.localization,
        attr.defaults,
        attr.validation,
        attr.unitFamily,
      );
    }
    if (attr.id === DefaultAttributeId.WALL_LENGTH) {
      return new AttributeModel(
        attr.id,
        attr.key,
        lengthValue,
        attr.predecessorId,
        EAttributeType.NUMBER,
        lengthUnit,
        attr.hidden,
        attr.forced,
        attr.ownerId,
        attr.userId,
        attr.publicFlag,
        attr.localization,
        attr.defaults,
        attr.validation,
        attr.unitFamily,
      );
    }
    return toAttributeModel(attr);
  });
}
export function mergeAttributes(existing: IAttribute[] = [], fetched: AttributeModel[] = []): AttributeModel[] {
  if (!existing.length) return fetched;
  
  // If no fetched attributes exist, just ensure the existing ones are AttributeModels
  if (!fetched.length) {
    return existing.map(a => 
      a instanceof AttributeModel 
        ? a 
        : new AttributeModel(a.id, a.key, a.value, a.predecessorId, a.type, a.unit, a.hidden, a.forced, a.ownerId, a.userId, a.publicFlag, a.localization, a.defaults, a.validation, a.unitFamily)
    );
  }

  const existingMap = new Map(existing.map((a) => [a.id, a]));
  const fetchedMap = new Map(fetched.map((a) => [a.id, a]));
  const merged: AttributeModel[] = [];

  // 1. Merge overlapping attributes: Keep EXISTING values, but use FETCHED definitions (hidden/forced)
  fetched.forEach((fAttr) => {
    if (existingMap.has(fAttr.id)) {
      const eAttr = existingMap.get(fAttr.id)!;
      merged.push(
        new AttributeModel(
          fAttr.id,
          fAttr.key,
          eAttr.value, // Keep the existing imported value!
          fAttr.predecessorId,
          fAttr.type,
          eAttr.unit !== undefined ? eAttr.unit : fAttr.unit, // Keep existing unit if available
          fAttr.hidden, // Enforce fetched flag
          fAttr.forced, // Enforce fetched flag
          fAttr.ownerId,
          fAttr.userId,
          fAttr.publicFlag,
          fAttr.localization,
          fAttr.defaults,
          fAttr.validation,
          fAttr.unitFamily
        )
      );
    } else {
      // It's a default/forced attribute that didn't exist on the object yet
      merged.push(fAttr);
    }
  });

  // 2. Append any custom existing attributes that weren't in the fetched template
  existing.forEach((eAttr) => {
    if (!fetchedMap.has(eAttr.id)) {
      merged.push(
        eAttr instanceof AttributeModel
          ? eAttr
          : new AttributeModel(eAttr.id, eAttr.key, eAttr.value, eAttr.predecessorId, eAttr.type, eAttr.unit, eAttr.hidden, eAttr.forced, eAttr.ownerId, eAttr.userId, eAttr.publicFlag, eAttr.localization, eAttr.defaults, eAttr.validation, eAttr.unitFamily)
      );
    }
  });

  return merged;
}
